import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { API_BASE_URL } from "../config/api";

/**
 * ============================================================================
 * FRONTEND REAL-TIME ARCHITECTURE: SSE (SERVER-SENT EVENTS) CONTEXT
 * ============================================================================
 * 
 * Purpose:
 *   Maintains a single persistent HTTP connection (`EventSource`) to the backend
 *   `/api/events` endpoint for streaming real-time notifications directly into the UI.
 * 
 * Functions & Components:
 *   1. RealtimeProvider:
 *      - Establishes `new EventSource('/api/events')`.
 *      - Receives streaming JSON events from the backend.
 *      - Dispatches decoupled window custom events (`rp:mandate_updated`, `rp:installment_updated`).
 *      - Automatically handles network disconnects with exponential backoff (3s, 6s, 12s, up to 30s).
 * 
 *   2. useMandateLiveUpdates(callback, filterRefNo, retailerId):
 *      - Subscribes any component (e.g. Mandate List, Mandate Detail page) to live mandate updates.
 *      - Updates component state optimistically without polling the server.
 * 
 *   3. useInstallmentLiveUpdates(callback, filterRefNo, retailerId):
 *      - Subscribes Installment tables and overview cards to live debit success/failure events.
 * 
 *   4. useRealtimeEvents():
 *      - Exposes `isConnected` and `connectionStatus` to show connection health indicators
 *        and control fallback polling timers.
 * ============================================================================
 */

export type ConnectionStatus = "connecting" | "connected" | "disconnected";

export interface RealtimeEvent<T = any> {
  type: "MANDATE_UPDATED" | "INSTALLMENT_UPDATED" | "WALLET_UPDATED" | "NOTIFICATION" | "CONNECTED";
  payload?: T;
  message?: string;
  timestamp: string;
}

interface RealtimeContextValue {
  /** Whether the SSE stream is currently connected */
  isConnected: boolean;
  /** Detailed connection status for UI indicators */
  connectionStatus: ConnectionStatus;
  /** The most recent event received */
  lastEvent: RealtimeEvent | null;
  /** Manually trigger a reconnect */
  reconnect: () => void;
}

const RealtimeEventsContext = createContext<RealtimeContextValue>({
  isConnected: false,
  connectionStatus: "disconnected",
  lastEvent: null,
  reconnect: () => {},
});

// Exponential backoff: 3s, 6s, 12s, 24s, then caps at 30s
function getBackoffMs(attempt: number): number {
  return Math.min(3000 * Math.pow(2, attempt), 30000);
}

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("disconnected");
  const [lastEvent, setLastEvent] = useState<RealtimeEvent | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);
  const unmountedRef = useRef(false);

  const connect = useCallback(() => {
    if (unmountedRef.current) return;

    // Close existing connection cleanly
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setConnectionStatus("connecting");

    // Build SSE URL — strip trailing /api to get the base server URL
    const baseUrl = API_BASE_URL.replace(/\/api\/?$/, "");
    const eventsUrl = `${baseUrl}/api/events`;

    try {
      // NOTE: Do NOT pass `{ withCredentials: true }` — SSE doesn't need cookie auth
      // and withCredentials can block the connection through ngrok / reverse proxies.
      const es = new EventSource(eventsUrl);
      eventSourceRef.current = es;

      es.onopen = () => {
        if (unmountedRef.current) return;
        // Reset backoff counter on successful connection
        attemptRef.current = 0;
        setIsConnected(true);
        setConnectionStatus("connected");
      };

      es.onmessage = (e) => {
        if (unmountedRef.current) return;
        try {
          // Skip heartbeat comment lines (": keepalive")
          if (!e.data || e.data.startsWith(":")) return;
          const data: RealtimeEvent = JSON.parse(e.data);

          // Don't surface the internal CONNECTED handshake as an event
          if (data.type === "CONNECTED") {
            setIsConnected(true);
            setConnectionStatus("connected");
            return;
          }

          setLastEvent(data);

          // Dispatch decoupled window custom events so any component can listen
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("rp:realtime_event", { detail: data }));
            if (data.type === "MANDATE_UPDATED") {
              window.dispatchEvent(new CustomEvent("rp:mandate_updated", { detail: data.payload }));
            } else if (data.type === "INSTALLMENT_UPDATED") {
              window.dispatchEvent(new CustomEvent("rp:installment_updated", { detail: data.payload }));
            } else if (data.type === "WALLET_UPDATED") {
              window.dispatchEvent(new CustomEvent("rp:wallet_updated", { detail: data.payload }));
            }
          }
        } catch (_err) {
          // Silently ignore parse errors (e.g., from heartbeat comment lines)
        }
      };

      es.onerror = () => {
        if (unmountedRef.current) return;
        setIsConnected(false);
        setConnectionStatus("disconnected");

        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }

        // Exponential backoff reconnect
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        const delay = getBackoffMs(attemptRef.current);
        attemptRef.current = Math.min(attemptRef.current + 1, 5);
        console.log(`[SSE] Disconnected. Reconnecting in ${delay}ms (attempt ${attemptRef.current})...`);
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      };
    } catch (_err) {
      setIsConnected(false);
      setConnectionStatus("disconnected");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    unmountedRef.current = false;
    connect();
    return () => {
      unmountedRef.current = true;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [connect]);

  const reconnect = useCallback(() => {
    // Manual reconnect: reset backoff
    attemptRef.current = 0;
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    connect();
  }, [connect]);

  return (
    <RealtimeEventsContext.Provider value={{ isConnected, connectionStatus, lastEvent, reconnect }}>
      {children}
    </RealtimeEventsContext.Provider>
  );
}

export function useRealtimeEvents() {
  return useContext(RealtimeEventsContext);
}

/**
 * Hook to listen for live mandate updates in real-time.
 *
 * @param callback - Called with the event payload whenever a relevant mandate update arrives.
 * @param filterIdOrRef - Optional mandate ID, refNo, or rocketpayMandateId to filter on.
 *                        When provided, only events matching this identifier trigger the callback.
 * @param retailerId - Optional retailer ID. When provided, only events for this retailer fire the callback.
 */
export function useMandateLiveUpdates(
  callback: (payload: any) => void,
  filterIdOrRef?: string,
  retailerId?: string | null
) {
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;

      // Retailer-scoped filtering — skip events belonging to other retailers
      if (retailerId && detail.retailerId && detail.retailerId !== retailerId) return;

      // Mandate ID / refNo filtering
      if (filterIdOrRef) {
        const matches =
          detail.mandateId === filterIdOrRef ||
          detail.refNo === filterIdOrRef ||
          detail.rocketpayMandateId === filterIdOrRef ||
          detail.mandate?.id === filterIdOrRef ||
          detail.mandate?.refNo === filterIdOrRef ||
          detail.mandate?.rocketpayMandateId === filterIdOrRef;
        if (!matches) return;
      }

      cbRef.current(detail);
    };

    window.addEventListener("rp:mandate_updated", handler);
    return () => {
      window.removeEventListener("rp:mandate_updated", handler);
    };
  }, [filterIdOrRef, retailerId]);
}

/**
 * Hook to listen for live installment updates in real-time.
 *
 * @param callback - Called with the event payload whenever a relevant installment update arrives.
 * @param filterMandateIdOrRef - Optional mandate ID or refNo to filter on.
 * @param retailerId - Optional retailer ID. When provided, only events for this retailer fire the callback.
 */
export function useInstallmentLiveUpdates(
  callback: (payload: any) => void,
  filterMandateIdOrRef?: string,
  retailerId?: string | null
) {
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;

      // Retailer-scoped filtering
      if (retailerId && detail.retailerId && detail.retailerId !== retailerId) return;

      // Mandate filter for installment events
      if (filterMandateIdOrRef) {
        const matches =
          detail.mandateId === filterMandateIdOrRef ||
          detail.mandateRefNo === filterMandateIdOrRef ||
          detail.refNo === filterMandateIdOrRef ||
          detail.installment?.mandateId === filterMandateIdOrRef ||
          detail.installment?.mandateRefNo === filterMandateIdOrRef ||
          detail.installmentId === filterMandateIdOrRef ||
          detail.rocketpayInstallmentId === filterMandateIdOrRef;
        if (!matches) return;
      }

      cbRef.current(detail);
    };

    window.addEventListener("rp:installment_updated", handler);
    return () => {
      window.removeEventListener("rp:installment_updated", handler);
    };
  }, [filterMandateIdOrRef, retailerId]);
}
